import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Chatv1Error, createBoard, listBoards, renameBoard, type BoardInfo } from '../chatv1/transport'
import { onChatv1Frame, onChatv1Open } from '../chatv1/ws'
import { Icon } from '../ui/Icon'
import { MAIN_BOARD } from './boardNav'
import { isNews, withSeen, withWrite } from './boardSeen'
import { mayChange } from './EditLock'

/**
 * THE ROOM'S BOARDS (owner, 2026-10-30: 讓 white board 可以更改名稱。並且新增不同的 board. 有 popup menu 可以讓使用者
 * 選擇 board). The list lives in the query cache under ['boards', conv]: read when the board opens and again when the
 * socket comes back, and changed by the room's `boards` frames, so a board someone makes or renames shows in every
 * member's menu at once.
 */

/** The room's first board's name until someone names it (chat lambda/boards.ts MAIN_NAME). */
export const MAIN_NAME = 'Whiteboard'

/**
 * A board made or renamed, into the list: replaced where it was, else added; main first, then in the order made.
 * How far this person has seen it stays: a frame is the room's, and never says that.
 */
export function mergeBoards(cur: BoardInfo[], changed: BoardInfo[]): BoardInfo[] {
  const byId = new Map(cur.map((b) => [b.board, b]))
  for (const b of changed) {
    const seen = b.seen ?? byId.get(b.board)?.seen
    byId.set(b.board, seen === undefined ? b : { ...b, seen })
  }
  return [...byId.values()].sort((a, b) =>
    a.board === MAIN_BOARD ? -1 : b.board === MAIN_BOARD ? 1 : a.created_ts - b.created_ts || (a.board < b.board ? -1 : 1),
  )
}

/** A name for a new board no other board has: "Whiteboard 2", "Whiteboard 3", … */
export function nextName(boards: BoardInfo[]): string {
  const taken = new Set(boards.map((b) => b.name.trim().toLowerCase()))
  for (let n = boards.length + 1; ; n++) {
    const name = `${MAIN_NAME} ${n}`
    if (!taken.has(name.toLowerCase())) return name
  }
}

export function useBoards(convId: string) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['boards', convId], queryFn: async () => (await listBoards(convId)).boards })
  useEffect(() => {
    const key = ['boards', convId]
    const offFrame = onChatv1Frame((f) => {
      if (f.type === 'boards' && f.conv_id === convId) qc.setQueryData<BoardInfo[]>(key, (cur) => mergeBoards(cur ?? [], f.boards))
      // Someone wrote to a board, or this person saw one on another device: the red dot (boardSeen.ts).
      if (f.type === 'board' && f.conv_id === convId) qc.setQueryData<BoardInfo[]>(key, (cur) => (cur ? withWrite(cur, f) : cur))
      if (f.type === 'board_seen' && f.conv_id === convId) {
        qc.setQueryData<BoardInfo[]>(key, (cur) => (cur ? withSeen(cur, f.board, f.rev) : cur))
      }
    })
    const offOpen = onChatv1Open(() => void qc.invalidateQueries({ queryKey: key }))
    return () => {
      offFrame()
      offOpen()
    }
  }, [convId, qc])
  /** Put an answer the service just gave into the list (the frame for it may come a moment later, or never). */
  const put = (b: BoardInfo) => qc.setQueryData<BoardInfo[]>(['boards', convId], (cur) => mergeBoards(cur ?? [], [b]))
  return { boards: q.data ?? null, put }
}

function failText(e: unknown): string {
  const code = e instanceof Chatv1Error ? e.code : ''
  if (code === 'too_many_boards') return 'This project has 50 boards — rename one instead.'
  if (code === 'invalid_name') return 'A name is one line of up to 80 characters.'
  if (code === 'not_a_member') return 'You are no longer in this project.'
  if (code === 'locked') return 'This board is locked: only the people its owner chose can change it.'
  return 'That did not save — check the connection and try again.'
}

/**
 * The menu under the board's name: every board of the room (the open one ticked, one changed since this person last
 * looked dotted red), a pencil to rename each, and a new board at the bottom — named as it is made, then opened.
 */
export function BoardMenu({
  convId,
  project,
  boards,
  current,
  me,
  anchor,
  phone,
  onPick,
  onPut,
  onClose,
}: {
  convId: string
  project: string
  boards: BoardInfo[]
  current: string
  /** This person (the chat's user id): their own hand's writes are no news to them. */
  me: string
  anchor: HTMLElement | null
  phone: boolean
  onPick: (board: string) => void
  onPut: (b: BoardInfo) => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [making, setMaking] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [pos, setPos] = useState<React.CSSProperties>({})

  // Under the name it opened from; on a phone, the width of the screen.
  useLayoutEffect(() => {
    const r = anchor?.getBoundingClientRect()
    if (!r) return
    setPos(phone ? { left: 8, right: 8, top: r.bottom + 6 } : { left: Math.max(8, r.left), top: r.bottom + 6 })
  }, [anchor, phone])

  useEffect(() => {
    const down = (e: PointerEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t) || anchor?.contains(t)) return
      onClose()
    }
    window.addEventListener('pointerdown', down, true)
    return () => window.removeEventListener('pointerdown', down, true)
  }, [anchor, onClose])

  const save = async () => {
    const name = draft.replace(/\s+/g, ' ').trim()
    if (!name || busy) return
    setBusy(true)
    setErr(null)
    try {
      if (making) {
        const { board } = await createBoard(convId, name)
        onPut(board)
        onPick(board.board)
        onClose()
        return
      }
      if (renaming) {
        const was = boards.find((b) => b.board === renaming)
        if (was?.name !== name) onPut((await renameBoard(convId, renaming, name)).board)
        setRenaming(null)
      }
    } catch (e) {
      setErr(failText(e))
    } finally {
      setBusy(false)
    }
  }

  const field = (label: string) => (
    <input
      className="bd-menu-input"
      autoFocus
      value={draft}
      maxLength={80}
      aria-label={label}
      placeholder="Board name"
      disabled={busy}
      onFocus={(e) => e.target.select()}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          void save()
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          setRenaming(null)
          setMaking(false)
          setErr(null)
        }
      }}
    />
  )

  return (
    <div
      ref={ref}
      className="bd-menu"
      style={pos}
      role="dialog"
      aria-label={`Boards in ${project}`}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <div className="bd-menu-head">Boards in {project}</div>
      <ul className="bd-menu-list">
        {boards.map((b) => (
          <li key={b.board} className={`bd-menu-row${b.board === current ? ' on' : ''}`}>
            {renaming === b.board ? (
              <>
                {field(`New name for ${b.name}`)}
                <button type="button" className="bd-menu-icon" aria-label="Save the name" disabled={busy} onClick={() => void save()}>
                  <Icon name="check" size={18} />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="bd-menu-pick"
                  aria-current={b.board === current ? 'true' : undefined}
                  onClick={() => {
                    onPick(b.board)
                    onClose()
                  }}
                >
                  <span className="bd-menu-tick">{b.board === current && <Icon name="check" size={16} />}</span>
                  <span className="bd-menu-name">{b.name}</span>
                  {isNews(b, me) && <span className="bd-news-dot" role="img" aria-label="Changed since you last looked" />}
                  {b.edit && <Icon name="lock" size={14} label="Locked" className="bd-menu-lock" />}
                </button>
                {mayChange(b, me) && (
                  <button
                    type="button"
                    className="bd-menu-icon"
                    title="Rename"
                    aria-label={`Rename ${b.name}`}
                    onClick={() => {
                      setMaking(false)
                      setErr(null)
                      setRenaming(b.board)
                      setDraft(b.name)
                    }}
                  >
                    <Icon name="edit" size={16} />
                  </button>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
      <div className="bd-menu-foot">
        {making ? (
          <div className="bd-menu-row">
            {field('Name of the new board')}
            <button type="button" className="bd-menu-make" disabled={busy || !draft.trim()} onClick={() => void save()}>
              Create
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="bd-menu-new"
            onClick={() => {
              setRenaming(null)
              setErr(null)
              setMaking(true)
              setDraft(nextName(boards))
            }}
          >
            <Icon name="add" size={18} />
            <span>New board</span>
          </button>
        )}
      </div>
      {err && (
        <div className="bd-menu-err" role="alert">
          {err}
        </div>
      )}
    </div>
  )
}
